import Slack from '~/assets/icon-slack.svg?react';
import { Pill, Pills, DetailsPopover, Tabs, Tab, BillableIcon } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useAuth, useIsMounted } from '~/hooks';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import Avatar from './Avatar';

const Links = styled.div`
  position: relative;
  width: 100%;
  padding-top: 1rem;

  > div {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
`;

const Figure = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
`;

const Content = styled.div`
  padding: 1.25rem 0.5rem;
  width: 100%;
`;

const Info = styled.div`
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;

  &:first-child {
    margin-top: 0;
  }
`;

const InfoBlockTitle = styled.h4`
  font-size: 0.75rem;
  font-weight: ${weights.black};
  color: ${colors.grey40};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  text-align: center;
`;

const InfoBlockContent = styled.div`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  flex: 1;
  text-align: center;
`;

const MemberTypeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
`;

function InfoBlock({ title, children, ...props }) {
  return (
    <Info {...props}>
      <InfoBlockTitle>{title}</InfoBlockTitle>
      <InfoBlockContent>{children}</InfoBlockContent>
    </Info>
  );
}

const Label = styled.div`
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.75rem;
`;

function MemberContactPopover({ member, placement = 'auto', delay: defaultDelay, ...props }) {
  const [visible, setVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const hovering = useRef(false);
  const forceVisible = useRef(false);
  const hoverHandler = useRef(null);
  const isMounted = useIsMounted();

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
  });

  const auth = useAuth();
  const { workspace } = useWorkspace();
  const [query, setQuery] = useState({ isReady: false, data: null });
  const api = useApi();

  useEffect(() => {
    if (!visible || query.isReady) return;

    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).members(member.id).contactCard();
        setQuery({ isReady: true, data });
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, member.id, visible, query.isReady]);

  const handleMouseEnter = () => {
    hovering.current = true;
    // If the query has been executed, delay showing the popover.
    // Otherwise, the delay will be caused by the API query.
    const delay = defaultDelay ?? (query.isReady ? 100 : 0);

    if (hoverHandler.current) clearTimeout(hoverHandler.current);
    hoverHandler.current = () => setVisible(true);

    setTimeout(() => {
      if (hoverHandler.current && isMounted.current) hoverHandler.current();
      if (!hovering.current) return;
    }, delay);
  };

  const handleMouseLeave = () => {
    hovering.current = false;

    setTimeout(() => {
      if (!forceVisible.current) setVisible(false);
    }, 200);

    if (hoverHandler.current) {
      clearTimeout(hoverHandler.current);
      hoverHandler.current = null;
    }
  };

  const handleCardMouseEnter = () => {
    forceVisible.current = true;
  };

  const handleCardMouseLeave = () => {
    forceVisible.current = false;
    setVisible(false);
  };

  let contact = { ...member, ...(query.data || {}) };
  const showSkillsAndTagsTab =
    contact.skills?.length > 0 || contact.tags?.length > 0 || (auth.allocations.view && contact.resume);

  return (
    <>
      <span ref={setReferenceElement} {...props} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
      {visible &&
        query.isReady &&
        ReactDOM.createPortal(
          <DetailsPopover
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}>
            <Links>
              <div>
                {contact.slackUserId && contact.workspace.slackAuthorization?.teamId && (
                  <a
                    href={`slack://user?team=${contact.workspace.slackAuthorization.teamId}&id=${contact.slackUserId}`}>
                    <Slack style={{ width: '1rem', height: '1rem' }} />
                  </a>
                )}
              </div>
            </Links>

            <Tabs selectedIndex={tabIndex} onChange={setTabIndex}>
              <Tab>General</Tab>
              {showSkillsAndTagsTab && <Tab>Skills & Tags</Tab>}
            </Tabs>

            <Content>
              {tabIndex === 0 && (
                <>
                  <Figure>
                    <Avatar value={contact} isCircle hasBackground size={70} />
                    <Label>{contact.name}</Label>
                  </Figure>
                  {contact.email && (
                    <InfoBlock title="Email">
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </InfoBlock>
                  )}
                  <InfoBlock title="Status">{contact.isActive ? 'Active' : 'Inactive'}</InfoBlock>
                  <InfoBlock title="Member Type">
                    <MemberTypeContainer>
                      {contact.employmentType?.name} <BillableIcon value={contact.isBillable} />
                    </MemberTypeContainer>
                  </InfoBlock>
                  {contact.jobTitle && <InfoBlock title="Job Title">{contact.jobTitle.name}</InfoBlock>}
                  {contact.level && <InfoBlock title="Level">{contact.level.name}</InfoBlock>}
                  {contact.location && <InfoBlock title="Location">{contact.location.name}</InfoBlock>}
                  {contact.practice && <InfoBlock title="Practice">{contact.practice.name}</InfoBlock>}
                  {contact.discipline && <InfoBlock title="Discipline">{contact.discipline.name}</InfoBlock>}
                  {contact.manager && <InfoBlock title="Manager">{contact.manager.name}</InfoBlock>}
                </>
              )}

              {tabIndex === 1 && (
                <>
                  {auth.allocations.view && contact.resume && (
                    <InfoBlock title="Resume">
                      <a target="_blank" rel="noreferrer" href={`${contact.resume.url}?download=1`}>
                        Download Resume
                      </a>
                    </InfoBlock>
                  )}
                  {contact.skills?.length > 0 && (
                    <InfoBlock title="Skills">
                      <Pills style={{ justifyContent: 'center' }}>
                        {contact.skills.map((skill) => (
                          <Pill key={skill.id}>{skill.name}</Pill>
                        ))}
                      </Pills>
                    </InfoBlock>
                  )}
                  {contact.tags?.length > 0 && (
                    <InfoBlock title="Tags">
                      <Pills style={{ justifyContent: 'center' }}>
                        {contact.tags.map((tag) => (
                          <Pill key={tag.id}>{tag.name}</Pill>
                        ))}
                      </Pills>
                    </InfoBlock>
                  )}
                </>
              )}
            </Content>
          </DetailsPopover>,
          document.body,
        )}
    </>
  );
}

export default MemberContactPopover;
